'use client';

import React, { useState } from 'react';
import { Routes } from '@/routes';
import { IBackgroundImages, INews } from '@/api/home/types';
import {
  IRegionAllPathsInfo,
  IRegionResortsNonPaginated,
  IResortShortReport,
} from '@/api/region/types';
import { BrowseByGroup, BrowseSection } from '@/components/global/BrowseByGroup';
import { AdUnitContext } from '@/tracking/ads/components/AdUnitContext';
import { TopAd } from '@/tracking/ads/components/TopAd';
import { RegularAd } from '@/tracking/ads/components/RegularAd';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import IntegrityError from '@/page-components/400';
import { HeaderNav, HeaderNavCurLink } from '@/components/common/HeaderNav';
import { BottomMountains } from '@/components/blocks/BottomMountains';
import { Footer } from '@/components/blocks/Footer';
import { Container } from '@/components/ui/container';
import { NewsList } from '@/components/home/NewsList';
import { getPortalOptions } from '@/config/getPortalConfig';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { useDistanceFormatter } from '@/i18n/unitFormat';
import { DataTable } from '@/components/common/DataTable';
import { MobileDataCard } from '@/components/common/MobileDataCard';
import { OpenCount } from '@/components/common/OpenCount';
import { ResortNameLink } from '@/components/common/ResortNameLink';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { cn } from '@/util/cn';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { SectionCard } from '@/components/ui/section-card';

// Global "Snow Reports" landing page.
//
// Structure mirrors the migrated region/skireport at the section
// level — SeoSection title + intro, then a sortable resort table —
// but unlike the region page, this surface has no single region/
// resort context. So:
//   - No RegionHeader / SubNav.
//   - No sidebar; no featured webcam.
//   - The leaderboard spans every region we cover.
//   - A second "Browse by region" section maps the user out to the
//     per-region snow reports.
//
// Column set on the leaderboard matches region/skireport's Open table
// (Resort | Region | Base | Lifts | Runs | 72h | Forecast) so the
// page reads as one coherent product when users drill down. The region
// suffix is unconditional here — a global leaderboard always spans
// regions — but ResortNameLink still gates it to en-US, since the name it
// renders is the untranslated `region_en`.

export type SnowWeatherHomePageProps = {
  regions: IRegionAllPathsInfo,
  lastNewsWeather: Array<INews> | null,
  backgroundImage: IBackgroundImages,
  topSnowResorts: IRegionResortsNonPaginated,
} & RouteAndAnalyticsProps;

export const TEMPLATE_ID = '2.0';
export const TEMPLATE_NAME = 'Snow & Weather Home';

const INITIAL_LEADERBOARD_ROWS = 15;

// 72-hour forecast — first three daily entries from the API.
// Identical helper to region/skireport so the two surfaces produce
// matching values for the same resort.
const forecast72hCm = (resort: IResortShortReport): number | undefined => {
  const items = resort.forecast?.slice(0, 3);
  if (!items || items.length === 0) return undefined;
  return items.reduce((sum, d) => sum + (d.snow ?? 0), 0);
};

const Leaderboard = ({ resorts }: { resorts: Array<IResortShortReport> }) => {
  const { t } = useOTSTranslation('common');
  const dt = useDistanceFormatter('cmOrInchShort');
  const dash = t('common.dash');
  const [showAll, setShowAll] = useState(false);

  const visible = showAll ? resorts : resorts.slice(0, INITIAL_LEADERBOARD_ROWS);
  const hasMore = resorts.length > INITIAL_LEADERBOARD_ROWS;

  if (resorts.length === 0) return null;

  return (
    <section>
      <div className="mb-4 flex items-end justify-between gap-3">
        <Heading
          level={2}
        >
          {t('home.snowLeaderboard.title')}
        </Heading>
        <span className="hidden text-xs uppercase tracking-wider text-muted-foreground sm:block">
          {t('home.snowLeaderboard.sortedBy')}
        </span>
      </div>

      <div className="hidden md:block">
        <DataTable>
          <DataTable.Head>
            <DataTable.HeadRow>
              <DataTable.HeadCell>
                {t('region.liftTickets.resort')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right">
                {t('common.base')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right">
                {t('resort.skiReport.liftOpen')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right">
                {t('resort.skiReport.runsOpen')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right">
                {t('resort.skiReport.72hSnowfall')}
              </DataTable.HeadCell>
              <DataTable.HeadCell align="right">
                {t('region.skiReport.snowForecast')}
              </DataTable.HeadCell>
            </DataTable.HeadRow>
          </DataTable.Head>
          <DataTable.Body>
            {visible.map((r, i) => {
              const fc72 = forecast72hCm(r);
              return (
                <DataTable.Row key={r.uuid} zebra={i % 2 === 0}>
                  <DataTable.RowHeader>
                    <ResortNameLink
                      resort={r}
                      href={Routes.resortSnowReport(r)}
                      showRegion
                    />
                  </DataTable.RowHeader>
                  <DataTable.Cell align="right">
                    {r.snow?.base ? dt(r.snow.base) : dash}
                  </DataTable.Cell>
                  <DataTable.Cell align="right">
                    <OpenCount stat={r.lifts} dash={dash} />
                  </DataTable.Cell>
                  <DataTable.Cell align="right">
                    <OpenCount stat={r.runs} dash={dash} />
                  </DataTable.Cell>
                  <DataTable.Cell align="right" className="font-semibold">
                    {r.snow?.last72 ? dt(r.snow.last72) : dash}
                  </DataTable.Cell>
                  <DataTable.Cell align="right">
                    {fc72 ? dt(fc72) : dash}
                  </DataTable.Cell>
                </DataTable.Row>
              );
            })}
          </DataTable.Body>
        </DataTable>
      </div>

      <div className="md:hidden">
        <SectionCard as="div" padding="none" className="overflow-hidden">
          {visible.map((r) => {
            const fc72 = forecast72hCm(r);
            return (
              <MobileDataCard
                key={r.uuid}
                layout="grid"
                heading={(
                  <div>
                    <ResortNameLink
                      resort={r}
                      href={Routes.resortSnowReport(r)}
                      showRegion
                    />
                  </div>
                )}
              >
                <MobileDataCard.Stat
                  label={t('common.base')}
                  value={r.snow?.base ? dt(r.snow.base) : dash}
                />
                <MobileDataCard.Stat
                  label={t('resort.skiReport.72hSnowfall')}
                  value={r.snow?.last72 ? dt(r.snow.last72) : dash}
                />
                <MobileDataCard.Stat
                  label={t('region.skiReport.snowForecast')}
                  value={fc72 ? dt(fc72) : dash}
                />
              </MobileDataCard>
            );
          })}
        </SectionCard>
      </div>

      {hasMore && (
        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2',
              'text-sm font-semibold text-foreground transition hover:bg-muted',
            )}
          >
            {showAll ? t('home.snowLeaderboard.showFewer') : t('home.snowLeaderboard.showAll', { count: resorts.length })}
          </button>
        </div>
      )}
    </section>
  );
};

// Browse-by-region directory lives in @/components/global/BrowseByGroup —
// shared with the global /ski-resorts page so both surfaces use the
// same bordered-card chrome and region-link grid.

export default function SnowWeatherHomePage({
  regions, lastNewsWeather, backgroundImage, topSnowResorts,
}: SnowWeatherHomePageProps) {
  const { t } = useOTSTranslation('common');
  const { hasNews } = getPortalOptions();

  if (!backgroundImage) {
    return <IntegrityError />;
  }

  const resortRows = topSnowResorts?.data ?? [];
  const regionList = regions?.regions ?? [];

  // Group regions by their parent group id so we can render a labeled
  // section per geography (United States, Canada, Europe, ...). Groups
  // that have no matching regions are skipped by BrowseByGroup.
  const sections: Array<BrowseSection> = (regions?.groups ?? []).map((group) => ({
    group,
    regions: regionList.filter((r) => r.group === group.uuid),
  }));

  return (
    <AdUnitContext
      adType="home"
      adTKey="other home"
      meta={{
        Fs: undefined,
        Rs: undefined,
        Th: undefined,
        Tl: undefined,
        snow: undefined,
        Wc: undefined,
      }}
    >
      <HeaderNav current={HeaderNavCurLink.SNOW_REPORTS} />
      <TopAd id="skireport" />
      <Container width="xl-only" className="content-container">
        <div className="mt-4 space-y-10 px-4 pb-16 md:mt-8 md:px-0">
          <header className="space-y-4">
            <Heading
              level={1}
            >
              {t('snowWeather.home.heroTitle')}
            </Heading>
            <Text variant="default" className="text-base leading-relaxed text-foreground/85">
              {t('snowWeather.home.seoText')}
            </Text>
          </header>

          <Leaderboard resorts={resortRows} />

          <RegularAd isFirst />

          <BrowseByGroup sections={sections} linkFor={Routes.regionSnowReport} />

          {hasNews && lastNewsWeather && (
            <NewsList
              title={t('homePage.latestNews')}
              news={lastNewsWeather}
              weatherNews
            />
          )}

          <SkiGptPromotion
            templateId={TEMPLATE_ID}
          />
        </div>
      </Container>
      <BottomMountains />
      <Footer />
    </AdUnitContext>
  );
}

// fetchProps moved to ./<filename>.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
